import { format } from 'date-fns';
import { MatchData } from '@/interfaces/liveGamesType';
import { api, apiPublic } from '@/services/api';
import { GroupedMatches, TabType } from './types';

/**
 * Verifica se uma data está no passado
 */
export const isDateInPast = (date: Date): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const compareDate = new Date(date);
  compareDate.setHours(0, 0, 0, 0);
  return compareDate < today;
};

/**
 * Verifica se uma partida está ao vivo
 */
export const isMatchLive = (match: MatchData): boolean => {
  const status = match.match_status?.toLowerCase() || '';
  return status !== 'finished' && status !== 'ft' && !status.includes('pen') && !status.includes('aet');
};

/**
 * Filtra partidas com base em uma string de busca
 */
export const filterMatches = (matches: MatchData[], searchQuery: string): MatchData[] => {
  if (!searchQuery.trim()) return matches;

  const query = searchQuery.toLowerCase();
  return matches.filter(match =>
    match.match_hometeam_name?.toLowerCase().includes(query) ||
    match.match_awayteam_name?.toLowerCase().includes(query) ||
    match.league_name?.toLowerCase().includes(query) ||
    match.country_name?.toLowerCase().includes(query)
  );
};

/**
 * Obtém partidas de acordo com o tipo de aba selecionada
 */
export const getMatchesByTab = (
  tabValue: TabType,
  liveMatches: MatchData[],
  finishedMatches: MatchData[],
  favorites: string[]
): MatchData[] => {
  switch (tabValue) {
    case TabType.ALL:
      return [...liveMatches, ...finishedMatches];
    case TabType.LIVE:
      return liveMatches;
    case TabType.FINISHED:
      return finishedMatches;
    case TabType.FAVORITES:
      return [...liveMatches, ...finishedMatches].filter(
        match => favorites.includes(match.match_id || '')
      );
    default:
      return [...liveMatches, ...finishedMatches];
  }
};

/**
 * Agrupa partidas por liga
 */
export const groupMatchesByLeague = (matches: MatchData[]): GroupedMatches => {
  const groupedMatches: GroupedMatches = {};

  matches.forEach(match => {
    const leagueId = match.league_id || '';
    const leagueName = match.league_name || '';
    const country = match.country_name || '';

    if (!groupedMatches[leagueId]) {
      groupedMatches[leagueId] = {
        league: leagueName,
        country: country,
        matches: []
      };
    }

    groupedMatches[leagueId].matches.push(match);
  });

  return groupedMatches;
};

/**
 * Obtém estatísticas de uma partida
 */
export const getMatchStats = async (matchId: string, isAuthenticated: boolean) => {
  try {
    const apiClient = isAuthenticated ? api : apiPublic;
    const response = await apiClient.get(`/football/getLiveMatchStats`, {
      params: { matchId }
    });
    return response.data.body?.data || response.data.data;
  } catch (error) {
    console.error('Erro ao buscar estatísticas da partida:', error);
    return null;
  }
};

/**
 * Carregar partidas ao vivo da API
 */
export const fetchLiveMatches = async (isAuthenticated: boolean) => {
  try {
    const apiClient = isAuthenticated ? api : apiPublic;
    const response = await apiClient.get('/football/getLiveMatchesFree');
    return response.data.body?.data || response.data?.data || [];
  } catch (error) {
    console.error('Erro ao carregar partidas ao vivo:', error);
    return [];
  }
};

/**
 * Carregar partidas históricas da API
 */
export const fetchHistoricalMatches = async (isAuthenticated: boolean, date: Date) => {
  try {
    const apiClient = isAuthenticated ? api : apiPublic;
    const response = await apiClient.get('/football/getHistMatchesFree', {
      params: {
        from: format(date, 'yyyy-MM-dd'),
        to: format(date, 'yyyy-MM-dd'),
        pageSize: 20,
        page: 1
      }
    });
    return response.data.body?.data || response.data?.data || [];
  } catch (error) {
    console.error('Erro ao carregar partidas históricas:', error);
    return [];
  }
};

/**
 * Dados de ligas
 */
export const getMainLeagues = () => [
  { id: '1', name: 'Brasileirão Betano', country: 'Brasil', logo: 'https://apiv3.apifootball.com/badges/logo_leagues/44_brasileirao.png' },
  { id: '2', name: 'Champions League', country: 'Europa', logo: 'https://apiv3.apifootball.com/badges/logo_leagues/3_champions-league.png' },
  { id: '3', name: 'Europa League', country: 'Europa', logo: 'https://apiv3.apifootball.com/badges/logo_leagues/4_europa-league.png' },
  { id: '4', name: 'Premier League', country: 'Inglaterra', logo: 'https://apiv3.apifootball.com/badges/logo_leagues/152_premier-league.png' },
  { id: '5', name: 'LaLiga', country: 'Espanha', logo: 'https://apiv3.apifootball.com/badges/logo_leagues/302_laliga.png' },
  { id: '6', name: 'Serie A', country: 'Itália', logo: 'https://apiv3.apifootball.com/badges/logo_leagues/207_serie-a.png' },
  { id: '7', name: 'Bundesliga', country: 'Alemanha', logo: 'https://apiv3.apifootball.com/badges/logo_leagues/175_bundesliga.png' },
];